import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Consultor {
  id: string;
  nome: string;
  celular?: string;
  cpf_cnpj?: string;
  email?: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

export function useConsultores() {
  return useQuery({
    queryKey: ['consultores'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('consultores')
        .select('*')
        .eq('ativo', true)
        .order('nome');

      if (error) {
        console.error('Erro ao buscar consultores:', error);
        throw error;
      }

      return data as Consultor[];
    },
  });
}

export function useCreateConsultor() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (consultor: Omit<Consultor, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('consultores')
        .insert(consultor)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultores'] });
      toast.success('Consultor cadastrado com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao cadastrar consultor');
    },
  });
}

export function useEditarConsultor() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (consultor: { id: string; nome: string; celular?: string; cpf_cnpj?: string; email?: string; ativo: boolean }) => {
      const { data, error } = await supabase
        .from('consultores')
        .update({
          nome: consultor.nome,
          celular: consultor.celular,
          cpf_cnpj: consultor.cpf_cnpj,
          email: consultor.email,
          ativo: consultor.ativo,
          updated_at: new Date().toISOString()
        })
        .eq('id', consultor.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultores'] });
      toast.success('Consultor editado com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao editar consultor');
    },
  });
}

export function useExcluirConsultor() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('consultores')
        .update({ ativo: false, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultores'] });
      toast.success('Consultor excluído com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao excluir consultor');
    },
  });
}